import JSZip from "jszip";
import { basenameOf, isDicomEntryName } from "./dicomFilenames";

// In-memory cache: resolved zip URL -> { files, blobUrls, refs }
//
// Entries are reference counted. Several viewers can show the same series (and
// React StrictMode mounts each one twice in development), so revoking blob URLs
// as soon as *one* consumer unmounts would leave the others holding dead
// blob: URLs — on which Cornerstone's `await viewport.setStack()` never settles,
// hanging the viewer on its loading overlay forever.
const zipCache = new Map();

// De-duplicates concurrent loads of the same URL, so a remount (or a second
// viewer on the same series) shares one download instead of fetching >100 MB twice.
const inFlight = new Map();

// Resolve the zip URL the same way for both load and revoke so cache keys match.
export function resolveZipUrl(seriesName, baseUrl, fullZipUrl) {
  return fullZipUrl || `${baseUrl}/images/dicom/${seriesName}.zip`;
}

/**
 * Streams the response so download progress is observable. Falls back to a plain
 * arrayBuffer() read when the body isn't streamable or the length is unknown.
 */
async function readWithProgress(response, report) {
  const total = Number(response.headers.get("Content-Length")) || 0;
  if (!response.body || !total) {
    report("download", 0, 0);
    return response.arrayBuffer();
  }

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    report("download", received, total);
  }

  const merged = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return merged.buffer;
}

/**
 * Downloads a series zip, extracts its DICOM instances, and creates blob URLs
 * for each. The blob URLs can be used as `wadouri:<blobUrl>` imageIds with
 * Cornerstone — the WADO loader fetches them from memory, not the network.
 *
 * The whole archive must arrive before the first slice can render, so callers
 * should surface `onProgress` rather than showing an unbounded spinner.
 *
 * @param {string} seriesName - e.g. "series1" or "series2"
 * @param {string} baseUrl - e.g. process.env.PUBLIC_URL or window.location.origin
 * @param {(p: {phase: "download"|"extract", loaded: number, total: number, pct: number}) => void} [onProgress]
 * @param {string} [fullZipUrl] - explicit asset URL (from the API); overrides the
 *   default /images/dicom/{seriesName}.zip path.
 */
/** Fetch, unzip, and turn each instance into a blob: URL Cornerstone can load. */
async function downloadAndExtract(zipUrl, seriesName, report) {
  const response = await fetch(zipUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch DICOM zip for ${seriesName}: ${response.status} ${response.statusText}`
    );
  }

  const arrayBuffer = await readWithProgress(response, report);
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Accept every name the backend's normalizer accepts — extensionless PACS
  // exports and UID-named files included, not just *.dcm.
  const fileEntries = Object.values(zip.files).filter(
    (f) => !f.dir && isDicomEntryName(f.name)
  );
  if (fileEntries.length === 0) {
    throw new Error(`No DICOM instances found in the archive for ${seriesName}`);
  }

  const files = new Map();
  const blobUrls = new Map();

  let loaded = 0;
  for (const entry of fileEntries) {
    const buf = await entry.async("arraybuffer");
    const blob = new Blob([buf], { type: "application/dicom" });
    const blobUrl = URL.createObjectURL(blob);
    // Strip any directory prefix inside the zip.
    const filename = basenameOf(entry.name);
    files.set(filename, buf);
    blobUrls.set(filename, blobUrl);
    loaded++;
    report("extract", loaded, fileEntries.length);
  }

  return { files, blobUrls, refs: 0 };
}

/**
 * Downloads a series zip, extracts its DICOM instances, and creates blob URLs
 * for each. The blob URLs can be used as `wadouri:<blobUrl>` imageIds with
 * Cornerstone — the WADO loader fetches them from memory, not the network.
 *
 * The whole archive must arrive before the first slice can render, so callers
 * should surface `onProgress` rather than showing an unbounded spinner.
 *
 * Each successful call takes a reference on the cache entry; pair it with
 * revokeDicomZipCache() on unmount.
 *
 * @param {string} seriesName - e.g. "series1" or "series2"
 * @param {string} baseUrl - e.g. process.env.PUBLIC_URL or window.location.origin
 * @param {(p: {phase: "download"|"extract", loaded: number, total: number, pct: number}) => void} [onProgress]
 * @param {string} [fullZipUrl] - explicit asset URL (from the API); overrides the
 *   default /images/dicom/{seriesName}.zip path.
 */
export async function loadDicomZip(seriesName, baseUrl, onProgress, fullZipUrl) {
  // Cache by the resolved URL so the same series name across different cases
  // (with different assets) doesn't collide.
  const cacheKey = resolveZipUrl(seriesName, baseUrl, fullZipUrl);

  const cached = zipCache.get(cacheKey);
  if (cached) {
    cached.refs += 1;
    return cached;
  }

  // A load is already running for this URL (second viewer, or a StrictMode
  // remount): join it instead of downloading the same archive again.
  const pending = inFlight.get(cacheKey);
  if (pending) {
    const entry = await pending;
    entry.refs += 1;
    return entry;
  }

  const report = (phase, loaded, total) => {
    if (typeof onProgress !== "function") return;
    onProgress({
      phase,
      loaded,
      total,
      pct: total > 0 ? Math.round((loaded / total) * 100) : 0,
    });
  };

  const load = downloadAndExtract(cacheKey, seriesName, report).then((entry) => {
    zipCache.set(cacheKey, entry);
    return entry;
  });
  inFlight.set(cacheKey, load);

  try {
    const entry = await load;
    entry.refs += 1;
    return entry;
  } finally {
    inFlight.delete(cacheKey);
  }
}

/**
 * Human-readable label for a loadDicomZip progress event, for a viewer overlay.
 * A series can be >100 MB and must download fully before slice 1 renders, so the
 * wait needs to be legible rather than an unbounded spinner.
 */
export function formatLoadProgress(p) {
  if (!p) return "Chargement DICOM…";
  if (p.phase === "download") {
    return p.total > 0
      ? `Téléchargement de la série… ${p.pct}%`
      : "Téléchargement de la série…";
  }
  return `Préparation des coupes… ${p.loaded}/${p.total}`;
}

/**
 * Revokes all blob URLs for a series and removes it from the cache.
 * Call this when the viewer unmounts.
 */
export function revokeDicomZipCache(seriesName, baseUrl, fullZipUrl) {
  const cacheKey = resolveZipUrl(seriesName, baseUrl, fullZipUrl);
  const cached = zipCache.get(cacheKey);
  if (!cached) return;

  cached.refs -= 1;
  // Another viewer (or a StrictMode remount) is still using these blob URLs.
  if (cached.refs > 0) return;

  for (const url of cached.blobUrls.values()) {
    URL.revokeObjectURL(url);
  }
  zipCache.delete(cacheKey);
}
